/**
 * The week grid.
 *
 * Purely presentational: it owns drop registration and hover feedback, and
 * hands every real decision back to RosterPage through callbacks. That split is
 * what lets the optimistic mutations live in one place next to the query cache
 * instead of being scattered through cell components.
 *
 * Layout is one CSS grid per row rather than a table, because a table cell
 * cannot be a scroll container and the roster needs each cell to stack an
 * unbounded number of shifts without breaking row alignment.
 */
import { useEffect, useRef, type RefObject, useState } from 'react';
import { dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { CalendarOff, ChevronRight, Plus, Users } from 'lucide-react';
import type { Area, Shift, ShiftTemplate, StaffMember } from '../lib/api.ts';
import {
  DAY_SHORT,
  fmtDayMonth,
  fmtDuration,
  fmtMoney,
  isWeekend,
  today,
  type IsoDate,
} from '../lib/time.ts';
import { Avatar } from '../components/Bits.tsx';
import { ShiftCard } from './ShiftCard.tsx';
import { CELL_DROP, isRosterDrag, cellKey, type CellDrop } from './dnd.ts';
import { shiftCost, sumShifts, type Rates, type UnavailableIndex } from './rosterMath.ts';

export interface GridRow {
  key: string;
  userId: string | null;
  areaId: string | null;
  name: string;
  /** Second line under the name: the role, or a hint on the Open Shifts row. */
  sub: string;
  open?: boolean;
}

export interface GridGroup {
  key: string;
  /** Null in the People view, where there is no area heading. */
  title: string | null;
  rows: GridRow[];
}

interface CellProps {
  row: GridRow;
  date: IsoDate;
  shifts: Shift[];
  rates: Record<string, Rates>;
  areaNames: Map<string, string>;
  colourFor: (shift: Shift) => string | null;
  unavailable: UnavailableIndex;
  showCost: boolean;
  templateSelected: boolean;
  onOpenShift: (id: string) => void;
  onDeleteShift: (id: string) => void;
  onPlaceTemplate: (target: CellDrop) => void;
  /** Clicking an empty cell. The only discoverable way to add a shift. */
  onAddShift: (target: CellDrop) => void;
  /** False when the business has a single area, whose name on every card would
      repeat what the whole roster already is. */
  showArea: boolean;
}

function Cell({
  row,
  date,
  shifts,
  rates,
  areaNames,
  colourFor,
  unavailable,
  showCost,
  templateSelected,
  onOpenShift,
  onDeleteShift,
  onPlaceTemplate,
  onAddShift,
  showArea,
}: CellProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const blocked = unavailable.has(row.userId, date);
  const note = unavailable.noteFor(row.userId, date);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const payload: CellDrop = { type: CELL_DROP, userId: row.userId, areaId: row.areaId, date, blocked };
    return combine(
      dropTargetForElements({
        element: el,
        // Every cell accepts the drop even when the person is unavailable. The
        // alternative, refusing it, gives the boss a dead cell and no reason
        // why. Instead the drop lands, the drawer opens, the conflict is stated
        // and the boss can force it. Blocking without explaining is the failure
        // mode we are avoiding.
        canDrop: ({ source }) => isRosterDrag(source.data),
        getData: () => ({ ...payload }),
        onDragEnter: () => setOver(true),
        onDragLeave: () => setOver(false),
        onDrop: () => setOver(false),
      }),
      // A drag cancelled with Escape produces no drop-target event at all and
      // would otherwise leave a cell highlighted for the rest of the session.
      monitorForElements({
        canMonitor: ({ source }) => isRosterDrag(source.data),
        onDrop: () => setOver(false),
      }),
    );
  }, [row.userId, row.areaId, date, blocked]);

  const totals = sumShifts(shifts, rates);
  const empty = shifts.length === 0;
  const clickToPlace = templateSelected && !blocked;

  return (
    <div
      ref={ref}
      onDrop={(e) => {
        // pdnd handles the real drop; this only exists so a browser-native file
        // drag over the grid does not navigate the page away.
        e.preventDefault();
      }}
      className={`relative flex flex-col gap-1 p-1.5 ${blocked ? 'cell-unavailable' : ''} ${
        over ? (blocked ? 'cell-drop-blocked' : 'cell-drop-ok') : ''
      }`}
      style={{
        minHeight: 'var(--sp-row-h)',
        borderLeft: '1px solid var(--sp-hairline)',
        background: over ? undefined : isWeekend(date) && !blocked ? 'var(--sp-bg-sunken)' : undefined,
      }}
    >
      {shifts.map((s) => (
        <ShiftCard
          key={s.id}
          shift={s}
          colour={colourFor(s)}
          areaName={showArea && s.areaId ? (areaNames.get(s.areaId) ?? null) : row.open ? 'Open shift' : null}
          cost={shiftCost(s, rates)}
          showCost={showCost && !!s.userId}
          onOpen={onOpenShift}
          onDelete={onDeleteShift}
        />
      ))}

      {/* The inline explanation. It replaces the "why can I not do this" that a
          silently refused drop leaves behind. */}
      {blocked && (over || empty) ? (
        <div
          className="pointer-events-none flex items-start gap-1.5 px-1 py-0.5 text-[10.5px] font-semibold leading-tight"
          style={{ color: 'var(--sp-warn-strong)' }}
        >
          <CalendarOff size={11} aria-hidden="true" className="mt-px shrink-0" />
          <span>
            {row.name.split(' ')[0]} marked this day unavailable{note ? `: ${note}` : '.'}
            {over ? ' Drop to open the shift and decide.' : ''}
          </span>
        </div>
      ) : null}

      {empty && !blocked ? (
        <button
          type="button"
          onClick={() => {
            const target: CellDrop = { type: CELL_DROP, userId: row.userId, areaId: row.areaId, date, blocked };
            // With a template chosen this is the fast path and places it
            // straight away. Without one it opens the drawer so the boss can
            // set the times, rather than doing nothing at all.
            if (clickToPlace) onPlaceTemplate(target);
            else onAddShift(target);
          }}
          className="cell-add flex flex-1 items-center justify-center transition-opacity"
          style={{ borderRadius: 'var(--sp-radius-sm)', border: '1px dashed var(--sp-border-strong)' }}
          aria-label={`Add a shift for ${row.name} on ${fmtDayMonth(date)}`}
          data-tip={
            clickToPlace
              ? 'Place the selected template here|Or drag any template chip onto this cell.'
              : 'Add a shift|Click to set the times, or drag a template chip from the row above.'
          }
        >
          <Plus size={15} aria-hidden="true" style={{ color: 'var(--sp-text-faint)' }} />
        </button>
      ) : null}

      {/* Per-cell figure, quiet until you look for it. A boss reading down a
          person's row is reading hours; a boss reading across a day is reading
          cost, and the totals bar carries that. */}
      {!empty && shifts.length > 1 ? (
        <span className="tabular px-1 text-[10px] font-semibold text-faint">
          {fmtDuration(totals.minutes)}
          {showCost ? ` · ${fmtMoney(totals.cost, { cents: false })}` : ''}
        </span>
      ) : null}

    </div>
  );
}

export interface RosterGridProps {
  weekDates: IsoDate[];
  groups: GridGroup[];
  shiftsByCell: Map<string, Shift[]>;
  rates: Record<string, Rates>;
  areas: Area[];
  templates: ShiftTemplate[];
  staff: StaffMember[];
  unavailable: UnavailableIndex;
  showCost: boolean;
  templateSelected: boolean;
  onOpenShift: (id: string) => void;
  onDeleteShift: (id: string) => void;
  onDropOnCell: (target: CellDrop, source: unknown) => void;
  onPlaceTemplate: (target: CellDrop) => void;
  onAddShift: (target: CellDrop) => void;
  /** Handed in so the totals bar below can pan in step with the grid. */
  scrollRef?: RefObject<HTMLDivElement | null>;
}

export function RosterGrid(props: RosterGridProps) {
  const {
    weekDates,
    groups,
    shiftsByCell,
    rates,
    areas,
    templates,
    unavailable,
    showCost,
    templateSelected,
    onOpenShift,
    onDeleteShift,
    onDropOnCell,
    onPlaceTemplate,
    onAddShift,
  } = props;

  const ownScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = props.scrollRef ?? ownScrollRef;
  // A week is wider than a phone, and an overflow container gives no hint that
  // it holds more. Without a visible edge the grid reads as "there is no
  // Thursday" rather than "scroll to reach Thursday".
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      setEdges({ left: el.scrollLeft > 2, right: max > 2 && el.scrollLeft < max - 2 });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [scrollRef]);
  const todayIso = today();

  const areaNames = new Map<string, string>();
  const areaColours = new Map<string, string | null>();
  for (const a of areas) {
    areaNames.set(a.id, a.name);
    areaColours.set(a.id, a.colour);
  }

  /**
   * A shift's colour is the colour of the template it was made from.
   *
   * There is no templateId on a shift, and adding one would be a schema change
   * for something that is presentation only, so the match is on the times: a
   * shift running 7:30am to 4pm IS the Open shift as far as anybody reading the
   * grid is concerned, however it was created. An area colour is the fallback
   * for a shift whose times match nothing, which keeps a hand-typed one-off
   * visually attached to where it is worked rather than dropping it to grey.
   */
  const templateColours = new Map<string, string | null>();
  for (const t of templates) {
    templateColours.set(`${t.startTime.slice(0, 5)}-${t.endTime.slice(0, 5)}`, t.colour);
  }
  const colourFor = (s: Shift): string | null => {
    const match = templateColours.get(`${s.startTime.slice(0, 5)}-${s.endTime.slice(0, 5)}`);
    if (match !== undefined) return match;
    return s.areaId ? (areaColours.get(s.areaId) ?? null) : null;
  };

  // A long roster has to keep scrolling while a shift is held over its edge,
  // otherwise the only reachable rows are the ones that happened to be on
  // screen when the drag began.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    return combine(
      autoScrollForElements({ element: el }),
      monitorForElements({
        canMonitor: ({ source }) => isRosterDrag(source.data),
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0];
          if (!target) return;
          const data = target.data as unknown as CellDrop;
          if (data.type !== CELL_DROP) return;
          onDropOnCell(data, source.data);
        },
      }),
    );
  }, [onDropOnCell]);

  // One area is the same as no areas: naming it on every card is noise.
  const showArea = areas.filter((a) => a.active !== false).length > 1;

  const cols = `var(--sp-staff-col) repeat(7, minmax(var(--sp-day-col), 1fr))`;

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scrollRef} className="thin-scroll h-full overflow-auto">
      <div style={{ minWidth: 'var(--sp-roster-min-w)' }}>
        {/* Day header */}
        <div
          className="sticky top-0 z-10 grid"
          style={{
            gridTemplateColumns: cols,
            height: 'var(--sp-dayhead-h)',
            background: 'var(--sp-surface)',
            borderBottom: '1px solid var(--sp-border)',
          }}
        >
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Users size={15} aria-hidden="true" style={{ color: 'var(--sp-text-faint)' }} />
            <span className="text-[11px] font-bold uppercase tracking-wide text-faint">Team</span>
          </div>
          {/* Day names only. The figures live in the totals bar pinned to the
              bottom of the page, so the same number is never shown twice with a
              chance of disagreeing. */}
          {weekDates.map((d, i) => {
            const isToday = d === todayIso;
            return (
              <div
                key={d}
                // overflow-hidden matters on a phone: the day column is barely
                // 104px there, and without it the Today chip spilled over the
                // top of the next day's heading.
                className="flex min-w-0 items-baseline gap-1.5 overflow-hidden px-2.5 py-2.5"
                style={{
                  borderLeft: '1px solid var(--sp-hairline)',
                  background: isToday ? 'var(--sp-accent-soft)' : undefined,
                  // A bar along the top edge, so today reads from across the room.
                  boxShadow: isToday ? 'inset 0 3px 0 var(--sp-accent)' : undefined,
                }}
              >
                <span
                  className="shrink-0 text-[12.5px] font-extrabold"
                  style={{ color: isToday ? 'var(--sp-accent-strong)' : 'var(--sp-text)' }}
                >
                  {DAY_SHORT[i]}
                </span>
                <span className="truncate text-[11.5px] font-medium text-muted">{fmtDayMonth(d)}</span>
                {/* The tinted column already says which day is today, so the
                    chip is a desktop nicety rather than the signal itself. */}
                {isToday ? (
                  // Compact on purpose: at full chip size it pushed the date
                  // into an ellipsis even on a desktop column.
                  <span
                    className="chip chip-accent ml-auto hidden shrink-0 sm:inline-flex"
                    style={{ height: 18, padding: '0 6px', fontSize: 10 }}
                  >
                    Today
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        {groups.map((group) => (
          <div key={group.key}>
            {group.title ? (
              <div
                className="sticky z-[5] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide"
                style={{
                  top: 'var(--sp-dayhead-h)',
                  background: 'var(--sp-surface-2)',
                  color: 'var(--sp-text-muted)',
                  borderBottom: '1px solid var(--sp-hairline)',
                  borderTop: '1px solid var(--sp-border)',
                }}
              >
                {group.title}
              </div>
            ) : null}
            {group.rows.map((row) => (
              <div
                key={row.key}
                className="roster-row grid"
                style={{
                  gridTemplateColumns: cols,
                  borderBottom: '1px solid var(--sp-hairline)',
                  background: row.open ? 'var(--sp-surface-2)' : 'var(--sp-surface)',
                }}
              >
                <div className="row-name flex items-center gap-2.5 px-3 py-2">
                  {row.open ? (
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center"
                      style={{
                        borderRadius: 'var(--sp-radius-pill)',
                        border: '1px dashed var(--sp-border-strong)',
                        color: 'var(--sp-text-faint)',
                      }}
                    >
                      <Users size={15} />
                    </span>
                  ) : (
                    <Avatar name={row.name} size={32} />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-bold leading-tight text-ink sm:text-[13.5px]">
                      {row.name}
                    </span>
                    <span className="block truncate text-[11px] leading-tight text-faint">{row.sub}</span>
                  </span>
                </div>
                {weekDates.map((date) => (
                  <Cell
                    key={date}
                    row={row}
                    date={date}
                    shifts={shiftsByCell.get(cellKey(row.userId, row.areaId, date)) ?? []}
                    rates={rates}
                    areaNames={areaNames}
                    colourFor={colourFor}
                    unavailable={unavailable}
                    showCost={showCost}
                    templateSelected={templateSelected}
                    onOpenShift={onOpenShift}
                    onDeleteShift={onDeleteShift}
                    onPlaceTemplate={onPlaceTemplate}
                    onAddShift={onAddShift}
                    showArea={showArea}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      </div>

      {/* Edge markers. Purely a signal, never a target: pointer-events stay off
          so they cannot eat a drag aimed at the cell underneath. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 transition-opacity"
        style={{
          width: 26,
          opacity: edges.left ? 1 : 0,
          background: 'linear-gradient(to right, var(--sp-bg), transparent)',
          transitionDuration: 'var(--sp-exit)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 transition-opacity"
        style={{
          width: 26,
          opacity: edges.right ? 1 : 0,
          background: 'linear-gradient(to left, var(--sp-bg), transparent)',
          transitionDuration: 'var(--sp-exit)',
        }}
      />
      {edges.right ? (
        <span
          className="pointer-events-none absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center md:hidden"
          style={{
            borderRadius: 'var(--sp-radius-pill)',
            background: 'var(--sp-surface)',
            border: '1px solid var(--sp-border)',
            boxShadow: 'var(--sp-shadow-2)',
            color: 'var(--sp-text-muted)',
          }}
        >
          <ChevronRight size={15} aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}
